import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, Newspaper, Play, Star, Zap, ChevronRight,
  Building2, FileText, CreditCard, Globe, Landmark, 
  TrendingUp, Calendar, Award
} from "lucide-react";

/**
 * Banking Awareness & Current Affairs Hub
 * CA Sprint Engine implementation
 */
const BankingAwarenessPage = () => {
  const navigate = useNavigate();

  const capsuleTopics = [
    {
      id: 'rbi',
      name: 'RBI & Monetary Policy',
      description: 'Repo, CRR, SLR, MPC decisions',
      icon: Landmark,
      color: 'bg-blue-500',
      questions: 50,
      practiced: 0,
      importance: 'High'
    },
    {
      id: 'banking-acts',
      name: 'Banking Acts & Regulations',
      description: 'BR Act, RBI Act, NI Act, SARFAESI',
      icon: FileText,
      color: 'bg-purple-500',
      questions: 40,
      practiced: 0,
      importance: 'High'
    },
    {
      id: 'products',
      name: 'Banking Products',
      description: 'Accounts, FD, RD, Loans, Insurance',
      icon: CreditCard,
      color: 'bg-emerald-500',
      questions: 60,
      practiced: 0,
      importance: 'Medium'
    },
    {
      id: 'digital',
      name: 'Digital Banking',
      description: 'UPI, NEFT, RTGS, IMPS, CBDC',
      icon: Globe,
      color: 'bg-teal-500',
      questions: 45,
      practiced: 0,
      importance: 'High'
    },
    {
      id: 'schemes',
      name: 'Government Schemes',
      description: 'PMJDY, Mudra, Stand Up India',
      icon: Award,
      color: 'bg-amber-500',
      questions: 55,
      practiced: 0,
      importance: 'High'
    },
    {
      id: 'institutions',
      name: 'Financial Institutions',
      description: 'NABARD, SIDBI, EXIM, NHB',
      icon: Building2,
      color: 'bg-indigo-500',
      questions: 35,
      practiced: 0,
      importance: 'Medium'
    },
    {
      id: 'markets',
      name: 'Financial Markets',
      description: 'Money, Capital, Forex, Derivatives',
      icon: TrendingUp,
      color: 'bg-pink-500',
      questions: 40,
      practiced: 0,
      importance: 'Medium'
    },
    {
      id: 'current',
      name: 'Current Banking Affairs',
      description: 'Latest news, appointments, mergers',
      icon: Calendar,
      color: 'bg-rose-500',
      questions: 100,
      practiced: 0,
      importance: 'High'
    }
  ];

  // Today's capsule preview
  const todayCapsule = {
    date: new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' }),
    items: [
      { type: 'RBI', text: 'RBI keeps repo rate unchanged at 6.5% in Dec 2025 MPC meet' },
      { type: 'Scheme', text: 'PM Vishwakarma Scheme extended to 2027 with Rs 13,000 cr allocation' },
      { type: 'Appointment', text: 'New MD & CEO of Bank of Baroda appointed' },
      { type: 'Digital', text: 'UPI transactions cross 15 billion mark in November 2025' },
      { type: 'International', text: 'India-UAE trade settlement in Rupee crosses $5 billion' },
    ]
  };

  const getImportanceColor = (imp) => {
    switch(imp) {
      case 'High': return 'bg-red-100 text-red-700';
      case 'Medium': return 'bg-amber-100 text-amber-700';
      case 'Low': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/banking')}
              className="bg-white/10 hover:bg-white/20 text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Newspaper className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Banking Awareness</h1>
                <p className="text-amber-200 text-sm">40 Marks in Mains | Critical for Interview</p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">0</p>
              <p className="text-xs text-amber-200">Capsules Read</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">0</p>
              <p className="text-xs text-amber-200">MCQs Done</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">0%</p>
              <p className="text-xs text-amber-200">Accuracy</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">8</p>
              <p className="text-xs text-amber-200">Topics</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        
        {/* Today's Capsule */}
        <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white border-0">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-400" />
                  Today's Banking Capsule
                </h3>
                <p className="text-slate-400 text-sm">{todayCapsule.date}</p>
              </div>
              <Button 
                className="bg-amber-500 hover:bg-amber-600 text-white"
                onClick={() => navigate('/banking/awareness/today')}
              >
                <Play className="w-4 h-4 mr-2" /> Read Capsule
              </Button>
            </div>
            <div className="space-y-2">
              {todayCapsule.items.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
                  <Badge className="bg-amber-500/20 text-amber-400 border-0 text-xs shrink-0">
                    {item.type}
                  </Badge>
                  <p className="text-sm text-slate-300">{item.text}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Quiz */}
        <Card className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 border-amber-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg text-slate-900">Banking Awareness Quiz</h3>
                <p className="text-slate-600 text-sm">Test your knowledge with 20 MCQs</p>
              </div>
              <Button 
                variant="outline"
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
                onClick={() => navigate('/banking/awareness/quiz')}
              >
                Take Quiz <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Topics Grid */}
        <div>
          <h2 className="text-lg font-bold text-slate-900 mb-4">Topic-wise Preparation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {capsuleTopics.map((topic) => (
              <Card
                key={topic.id}
                className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] border-2 border-transparent hover:border-amber-200"
                onClick={() => navigate(`/banking/awareness/topic/${topic.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-10 h-10 ${topic.color} rounded-lg flex items-center justify-center`}>
                      <topic.icon className="w-5 h-5 text-white" />
                    </div>
                    <Badge className={getImportanceColor(topic.importance)}>
                      {topic.importance}
                    </Badge>
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1">{topic.name}</h3>
                  <p className="text-xs text-slate-500 mb-3">{topic.description}</p>
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>{topic.practiced}/{topic.questions} done</span>
                    <span>{Math.round((topic.practiced/topic.questions)*100)}%</span>
                  </div>
                  <Progress value={(topic.practiced/topic.questions)*100} className="h-1.5 mt-2" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Key Static Facts */}
        <Card>
          <CardContent className="p-5">
            <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Must-Know Static Facts
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-500 mb-1">RBI Founded</p>
                <p className="font-bold text-slate-900">April 1, 1935</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-500 mb-1">RBI Headquarters</p>
                <p className="font-bold text-slate-900">Mumbai</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-500 mb-1">CRR Range</p>
                <p className="font-bold text-slate-900">3% - 15%</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-500 mb-1">Current Repo Rate</p>
                <p className="font-bold text-slate-900">6.5%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tips Section */}
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <h3 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
              <Star className="w-5 h-5" /> Banking Awareness Strategy
            </h3>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>- Read daily capsule without fail - 5 mins/day = 40 marks secured</li>
              <li>- Focus on last 6 months current affairs for banking</li>
              <li>- Remember all RBI rates, limits, and committees</li>
              <li>- Government schemes: Launch date, target group, implementing agency</li>
              <li>- Financial institutions: Headquarters, MD/CEO, taglines</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BankingAwarenessPage;
